import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Users, UserPlus, Handshake } from "lucide-react";
import { toast } from "sonner";

const STAGES = ["discovery", "proposal", "negotiation", "won", "lost"];
const STAGE_COLORS = {
  discovery: "#94A3B8", proposal: "#EAB308", negotiation: "#F97316",
  won: "#10B981", lost: "#EF4444",
};

export default function CRM() {
  const [tab, setTab] = useState("customers");
  const [customers, setCustomers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [deals, setDeals] = useState([]);

  const [openDlg, setOpenDlg] = useState(false);
  const [form, setForm] = useState({});

  const load = () => {
    api.get("/crm/customers").then((r) => setCustomers(r.data.customers));
    api.get("/crm/leads").then((r) => setLeads(r.data.leads));
    api.get("/crm/deals").then((r) => setDeals(r.data.deals));
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      if (tab === "customers") {
        await api.post("/crm/customers", { name: form.name, email: form.email, company: form.company, tags: [], notes: form.notes });
      } else if (tab === "leads") {
        await api.post("/crm/leads", { name: form.name, email: form.email, source: form.source || "manual", score: parseInt(form.score || 0) });
      } else {
        await api.post("/crm/deals", { title: form.title, value: parseFloat(form.value || 0), currency: "EUR", stage: "discovery" });
      }
      toast.success("Creado");
      setForm({});
      setOpenDlg(false);
      load();
    } catch {
      toast.error("Error al crear");
    }
  };

  const changeStage = async (deal, stage) => {
    await api.patch(`/crm/deals/${deal.id}/stage`, { stage });
    toast.success(`Deal → ${stage}`);
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="crm-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">SISTEMA COMERCIAL</div>
          <h1 className="font-display font-black text-4xl tracking-tight">CRM</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">Clientes, leads y oportunidades — datos operativos reales (Postgres/Mongo).</p>
        </div>
        <Dialog open={openDlg} onOpenChange={setOpenDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="crm-add">
              <Plus className="w-4 h-4 mr-2" /> Nuevo {tab === "customers" ? "cliente" : tab === "leads" ? "lead" : "deal"}
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <DialogHeader>
              <DialogTitle className="font-display">Crear {tab}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              {tab === "customers" && (
                <>
                  <F l="Nombre" v={form.name} on={(v) => setForm({ ...form, name: v })} t="c-name" />
                  <F l="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} t="c-email" />
                  <F l="Empresa" v={form.company} on={(v) => setForm({ ...form, company: v })} t="c-company" />
                  <F l="Notas" v={form.notes} on={(v) => setForm({ ...form, notes: v })} t="c-notes" />
                </>
              )}
              {tab === "leads" && (
                <>
                  <F l="Nombre" v={form.name} on={(v) => setForm({ ...form, name: v })} t="l-name" />
                  <F l="Email" v={form.email} on={(v) => setForm({ ...form, email: v })} t="l-email" />
                  <F l="Fuente" v={form.source} on={(v) => setForm({ ...form, source: v })} t="l-source" />
                  <F l="Score (0-100)" v={form.score} on={(v) => setForm({ ...form, score: v })} t="l-score" />
                </>
              )}
              {tab === "deals" && (
                <>
                  <F l="Título" v={form.title} on={(v) => setForm({ ...form, title: v })} t="d-title" />
                  <F l="Valor (EUR)" v={form.value} on={(v) => setForm({ ...form, value: v })} t="d-value" />
                </>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="crm-submit">
                Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none bg-[var(--surface)] border border-[var(--border)] p-0 h-auto">
          <TabsTrigger value="customers" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--accent)] font-mono uppercase text-xs" data-testid="tab-customers">
            <Users className="w-3 h-3 mr-2" /> Clientes ({customers.length})
          </TabsTrigger>
          <TabsTrigger value="leads" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--accent)] font-mono uppercase text-xs" data-testid="tab-leads">
            <UserPlus className="w-3 h-3 mr-2" /> Leads ({leads.length})
          </TabsTrigger>
          <TabsTrigger value="deals" className="rounded-none data-[state=active]:bg-[var(--surface-2)] data-[state=active]:text-[var(--accent)] font-mono uppercase text-xs" data-testid="tab-deals">
            <Handshake className="w-3 h-3 mr-2" /> Deals ({deals.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="customers" className="mt-4">
          <Table items={customers} cols={["name", "email", "company", "notes"]} empty="Sin clientes aún." testid="tbl-customers" />
        </TabsContent>
        <TabsContent value="leads" className="mt-4">
          <Table items={leads} cols={["name", "email", "source", "score", "status"]} empty="Sin leads aún." testid="tbl-leads" />
        </TabsContent>
        <TabsContent value="deals" className="mt-4">
          {deals.length === 0 ? (
            <Empty text="Sin deals aún." />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
              {deals.map((d) => (
                <div key={d.id} className="border border-[var(--border)] bg-[var(--surface)] p-4" data-testid={`deal-${d.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="font-display font-bold text-base">{d.title}</div>
                    <Badge className="rounded-none font-mono text-[10px]" style={{ background: STAGE_COLORS[d.stage], color: "#0B0E14" }}>
                      {d.stage.toUpperCase()}
                    </Badge>
                  </div>
                  <div className="font-mono text-2xl text-[var(--accent)] mb-3">
                    {d.value?.toLocaleString("es-ES") || 0} {d.currency}
                  </div>
                  <Select value={d.stage} onValueChange={(v) => changeStage(d, v)}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono text-xs" data-testid={`deal-stage-${d.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

const F = ({ l, v, on, t }) => (
  <div>
    <Label className="label-mono">{l}</Label>
    <Input value={v || ""} onChange={(e) => on(e.target.value)} className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid={t} />
  </div>
);

const Empty = ({ text }) => (
  <div className="border border-dashed border-[var(--border)] p-12 text-center text-sm text-[var(--text-dim)]">{text}</div>
);

const Table = ({ items, cols, empty, testid }) => (
  items.length === 0 ? <Empty text={empty} /> : (
    <div className="border border-[var(--border)] bg-[var(--surface)]" data-testid={testid}>
      <div className={`grid label-mono py-3 px-4 border-b border-[var(--border)] bg-[var(--surface-2)]`}
           style={{ gridTemplateColumns: `repeat(${cols.length}, 1fr)` }}>
        {cols.map((c) => <div key={c}>{c.toUpperCase()}</div>)}
      </div>
      {items.map((it) => (
        <div key={it.id} className="grid py-3 px-4 border-b border-[var(--border)] font-mono text-xs"
             style={{ gridTemplateColumns: `repeat(${cols.length}, 1fr)` }} data-testid={`row-${it.id}`}>
          {cols.map((c) => <div key={c} className="truncate text-[var(--text-dim)]">{String(it[c] || "—")}</div>)}
        </div>
      ))}
    </div>
  )
);
